import AddButton from "@components/AddButton/AddButton"
import Chevron from "@components/Chevron/Chevron"
import Modal from "@components/Modal/Modal"
import PlusIcon from "@components/PlusIcon/PlusIcon"
import AccountListItem from "@components/AccountListItem/AccountListItem"
import SearchBar from "@components/SearchBar/SearchBar"
import TrashIcon from "@components/TrashIcon/TrashIcon"
import StarIcon from "@components/StarIcon/StarIcon"
import DialogConfirmationBox from "@components/DialogConfirmationBox/DialogConfirmationBox"
import { BLUE, GRAYSCALE_400, GRAYSCALE_500, PALE_BLUE } from "@styles/colors"
import { useNavigate } from "react-router"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
    Container,
    EmptySelectionContainer,
    PlusIconContainer,
    SelectText,
    ModalContentContainer,
    ModalContent,
    SearchContainer,
    OptionsContainer,
    OptionItemContainer,
    OptionDivider,
    NoResultsText,
    ButtonContainer,
} from "./RedemptionTargetSelectPopUp.styles"
import { filterBySearchQuery } from "@lib/utils"
import { RedemptionOption } from "../../types/redemption"
import { useUserRestrictions } from "@hooks/useUserRestrictions"

const RedemptionTargetSelectPopUp = ({
    type,
    oneLine = false,
    options,
    preselect,
    onSelect,
    currentAmount,
    onRemove,
    isFavorite,
    onFavoriteToggle,
}: {
    type: 'bank' | 'e-wallet'
    oneLine?: boolean
    options: RedemptionOption[]
    preselect?: RedemptionOption
    onSelect?: (option: RedemptionOption) => void
    currentAmount?: number
    onRemove?: (id: string) => Promise<void>
    isFavorite?: (id: string) => boolean
    onFavoriteToggle?: (id: string) => void
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate()
    const [selectedOption, setSelectedOption] = useState<RedemptionOption | undefined>(preselect)
    const [isOpen, setIsOpen] = useState(false)
    const [isConfirmationDialogVisible, setIsConfirmationDialogVisible] = useState(false)
    const [toBeRemoved, setToBeRemoved] = useState('')

    const [query, setQuery] = useState('')

    const { canManageBankAccounts, canManageEwalletAccounts } = useUserRestrictions()

    const toggleModal = () => {
        setIsOpen(!isOpen)
    }

    const handlePromptConfirmation = (e: React.MouseEvent, id: string) => {
        e.stopPropagation()
        setIsOpen(false) // Close modal first
        setToBeRemoved(id)
        // Small delay to ensure modal closes before showing dialog
        setTimeout(() => {
            setIsConfirmationDialogVisible(true)
        }, 100)
    }

    const handleHideConfirmation = async () => {
        setIsConfirmationDialogVisible(false)
    }

    const handleConfirmRemove = async () => {
        if (onRemove && toBeRemoved) {
            await onRemove(toBeRemoved)
            // Clear selection if the removed item was selected
            if (selectedOption?.id === toBeRemoved) {
                setSelectedOption(undefined)
                onSelect?.(undefined as any)
            }
        }
        handleHideConfirmation()
    }

    const handleAddNew = () => {
        const params = new URLSearchParams({
            mode: 'add',
            source: type === 'bank' ? 'redeem-bank' : 'redeem-e-wallet'
        });

        if (currentAmount && currentAmount > 0) {
            params.append('amount', currentAmount.toString());
        }

        const basePath = type === 'bank' ? '/manage-bank' : '/manage-e-wallet';
        navigate(`${basePath}?${params.toString()}`);
    }

    // Only filter options when the modal is open
    const filteredOptions = isOpen
        ? filterBySearchQuery(options, query, ['name', 'accountNumber', 'nickname'])
        : options

    // Handle search input without closing the modal
    const handleSearchChange = (value: string) => {
        setQuery(value)
    }

    if (!options.length && ((type === 'bank' && canManageBankAccounts()) || (type === 'e-wallet' && canManageEwalletAccounts()))) {
        return <AddButton
            onClick={handleAddNew}
            label={type === 'bank' ? t('components.redemptionTargetSelectPopUp.addBankAccount') : t('components.redemptionTargetSelectPopUp.addEWallet')}
            size={10} fontSize={'13px'}

            $backgroundColor={PALE_BLUE}
            $borderColor={GRAYSCALE_500}
        />
    }

    return (
        <Container onClick={toggleModal} $hasSelectedOption={!!selectedOption}>
            {selectedOption &&
                <AccountListItem
                    {...selectedOption}
                    oneLine={oneLine}
                    tag={isOpen ? 'chevron-up' : 'chevron-down'}
                    onClick={() => {
                        toggleModal()
                    }}
                />
            }
            {!selectedOption && (
                <>
                    <EmptySelectionContainer>
                        <PlusIconContainer>
                            <PlusIcon color={GRAYSCALE_400} />
                        </PlusIconContainer>
                        <SelectText>{t('components.redemptionTargetSelectPopUp.selectOne')}</SelectText>
                    </EmptySelectionContainer>
                    <div>
                        <Chevron
                            direction={isOpen ? "up" : "down"}
                            color={BLUE}
                            strokeWidth={1}
                            size={10}
                        />
                    </div>
                </>
            )}
            <Modal isOpen={isOpen} onClose={toggleModal}>
                <ModalContentContainer onClick={(e) => e.stopPropagation()}>
                    <ModalContent>
                        <SearchContainer>
                            <SearchBar value={query} onChange={handleSearchChange} />
                        </SearchContainer>
                        <OptionsContainer>
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map((option, index) => (
                                    <OptionItemContainer key={index}>
                                        <AccountListItem
                                            oneLine={oneLine}
                                            {...option}
                                            tag="none"
                                            onClick={option.isValid ? (() => {
                                                onSelect?.(option)
                                                setSelectedOption(option)
                                                toggleModal()
                                            }) : () => { }}
                                            rightSlot={
                                                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', alignSelf: 'center', gap: '10px' }}>
                                                    {onFavoriteToggle && (
                                                        <button
                                                            type="button"
                                                            aria-label={`${isFavorite?.(option.id) ? 'Unfavorite' : 'Favorite'} ${option.id}`}
                                                            onClick={(e) => { e.stopPropagation(); onFavoriteToggle(option.id); }}
                                                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', padding: 0 }}
                                                        >
                                                            <StarIcon
                                                                filled={isFavorite?.(option.id)}
                                                                size={18}
                                                            />
                                                        </button>
                                                    )}
                                                    {onRemove && ((type === 'bank' && canManageBankAccounts()) || (type === 'e-wallet' && canManageEwalletAccounts())) && (
                                                        <button
                                                            type="button"
                                                            aria-label={`Delete ${option.id}`}
                                                            onClick={(e) => handlePromptConfirmation(e, option.id)}
                                                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', padding: 0 }}
                                                        >
                                                            <TrashIcon
                                                                iconWidth={14}
                                                                iconHeight={15}
                                                                iconColor="#ED5E62"
                                                            />
                                                        </button>
                                                    )}
                                                </div>
                                            }
                                        />
                                        {index !== filteredOptions.length - 1 && <OptionDivider />}
                                    </OptionItemContainer>
                                ))
                            ) : (
                                <NoResultsText>{t('components.redemptionTargetSelectPopUp.noResultsFound')}</NoResultsText>
                            )}
                        </OptionsContainer>
                        {((type === 'bank' && canManageBankAccounts()) || (type === 'e-wallet' && canManageEwalletAccounts())) && <ButtonContainer>
                            <AddButton
                                onClick={handleAddNew}
                                label={t('components.redemptionTargetSelectPopUp.addNew')}
                                size={8}
                                fontSize={'12px'}
                                $borderColor={GRAYSCALE_500}
                            />
                        </ButtonContainer>}
                    </ModalContent>
                </ModalContentContainer>
            </Modal>
            {isConfirmationDialogVisible && (
                <DialogConfirmationBox
                    isOpen={isConfirmationDialogVisible}
                    title={t(type === 'bank' ? 'bankAndAddress.bank.removeConfirmation.title' : 'bankAndAddress.ewallet.removeConfirmation.title')}
                    description={t(type === 'bank' ? 'bankAndAddress.bank.removeConfirmation.description' : 'bankAndAddress.ewallet.removeConfirmation.description')}
                    positiveButtonText={t(type === 'bank' ? 'bankAndAddress.bank.removeConfirmation.positiveButtonText' : 'bankAndAddress.ewallet.removeConfirmation.positiveButtonText')}
                    negativeButtonText={t(type === 'bank' ? 'bankAndAddress.bank.removeConfirmation.negativeButtonText' : 'bankAndAddress.ewallet.removeConfirmation.negativeButtonText')}
                    onPositiveClick={handleHideConfirmation}
                    onNegativeClick={handleConfirmRemove}
                    onOverlayClick={handleHideConfirmation}
                />
            )}
        </Container>
    )
}

export default RedemptionTargetSelectPopUp